import { FC, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ISummary } from 'src/types';
import { AIRequestForm } from 'src/containers/school/ai/aiRequestForm';
import { getFile } from 'src/services/schoolRequest';
import { useAuth } from 'src/contexts';
import {  IAIRequest } from 'src/types/org/ai/schoolInterface';
import { ErrorNotice, Loader } from 'src/generic/component';
import { APP_URLS } from 'src/constants/routes';
import { transformToIAIRequest } from 'src/utils/schoolCreateFormUtils';

const READ_ONLY_STATUSES = ['processing', 'appeal', 'international', 'testSecurity', 'opsApproved', 'completed', 'qcReadyVetting', 'qcReadyRejected', 'qcReadyReopen'];
const isReadOnly = (summary: ISummary) => {
  if (!summary) return false; // Add null check
  const { processStatus, appealEligible } = summary;
  const cannotResubmit = (processStatus === 'rejected' && !appealEligible);
  return (READ_ONLY_STATUSES.includes(processStatus) || cannotResubmit);
};

// Retrieve the AI form with a custom hook here...
const useFile = (file: string | undefined) => {
  const [data, setData] = useState<any | undefined>(undefined);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [errorLoading, setError] = useState<string>('');

  const retrieve = async (file: string) => {
    setLoading(true);
    try {
      const request = await getFile(file);
      setData(request);
    } catch (error: any) {
      setError(error?.message || error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (file) { // Only call retrieve if file exists
      retrieve(file);
    }
  }, [file]);

  return { data, setData, isLoading, errorLoading };
};

// Updated School.tsx
const School: FC = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();

  const location = useLocation();
  const state = location.state as any;
  const item = state?.summary as ISummary;
  const file = state?.file as string;

  useEffect(() => {
    if (!location.state) {
      navigate(APP_URLS.HOME);
    }
  }, [location.state, navigate]);

  const readOnly = isAdmin || (item ? isReadOnly(item) : false);
  const filePath = file || (item ? ((readOnly) ? (item.submission || item?.draft) : item?.draft) : undefined);

  const { data, isLoading, errorLoading } = useFile(filePath);

  if (isLoading) {
    return <Loader />;
  } else if (errorLoading) {
    return <ErrorNotice errorMessage={errorLoading} />;
  } else if (data) {
  if (item.appliedType === 'ai') {
  
  // Call transformToIAIRequest to map raw JSON from S3 into typescript IAIRequest format that React can work with
  const transformedRequest = transformToIAIRequest(data, item) as IAIRequest;
  
  return (
        <>
          {/* Red Text Banner */}
          <div style={{
            color: '#324DC7',
            padding: '12px 16px',
            margin: '0 0 20px 0',
            fontSize: '16px',
            fontWeight: 'bold',
            textAlign: 'left',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{
              backgroundColor: '#FF0000',
              color: 'white',
              borderRadius: '50%',
              width: '20px',
              height: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              fontWeight: 'bold'
            }}>
              !
            </span>
            College Board School Code requests must be submitted by the head of school or district. Requests submitted by unauthorized users cannot be processed.
          </div>

          <AIRequestForm
            readOnly={readOnly}
            request={transformedRequest}
            item={item}
          />
        </>
      );
    } else {
      return <div>Unknown Summary Record!</div>;
    }
  } else {
    return <Loader />;
  }
};
export default School;